import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  Eye,
  FileText,
  ListChecks,
  Receipt,
  ShoppingCart,
  Upload,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { notificar } from '@/shared/ui/avisos';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Cifra } from '@/modules/inicio/piezas';
import { enDinero } from '@/shared/ui/graficos';
import { DetalleDelComprobante } from './DetalleDelComprobante';
import { PanelDeImportacion } from './PanelDeImportacion';
import { PanelDelListado } from './PanelDelListado';
import { mensajeDe } from './mensajes';
import type {
  CatalogosDeCompra,
  Comprobante,
  EstadoDeCompra,
  ListaDeCompras,
  ResumenDeCompras,
} from './contrato';

function enFecha(iso: string | undefined): string {
  if (!iso) return '—';
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

function primeroDelMes(): string {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
}

function hoyIso(): string {
  const hoy = new Date();
  const dosCifras = (n: number) => String(n).padStart(2, '0');
  return `${hoy.getFullYear()}-${dosCifras(hoy.getMonth() + 1)}-${dosCifras(hoy.getDate())}`;
}

export function PaginaCompras() {
  const [texto, setTexto] = useState('');
  const [estado, setEstado] = useState<EstadoDeCompra | ''>('');
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoyIso());
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [importando, setImportando] = useState(false);
  const [cargandoListado, setCargandoListado] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [abierto, setAbierto] = useState<Comprobante | null>(null);
  const [anulando, setAnulando] = useState<Comprobante | null>(null);
  const [motivo, setMotivo] = useState('');

  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeGestionar = puede('compras.gestionar');

  const buscado = useValorDiferido(texto.trim());

  const listado = useQuery({
    queryKey: ['compras', buscado, estado, desde, hasta, soloPendientes, pagina, tamanio],
    queryFn: () =>
      api.get<ListaDeCompras>('/compras', {
        query: {
          texto: buscado,
          estado,
          desde,
          hasta,
          pendientesDeXml: String(soloPendientes),
          pagina,
          tamanio,
        },
      }),
    placeholderData: (anterior) => anterior,
  });

  const resumen = useQuery({
    queryKey: ['compras', 'resumen', desde, hasta],
    queryFn: () => api.get<ResumenDeCompras>('/compras/resumen', { query: { desde, hasta } }),
  });

  const catalogos = useQuery({
    queryKey: ['compras', 'opciones'],
    queryFn: () => api.get<CatalogosDeCompra>('/compras/opciones'),
    staleTime: Infinity,
  });

  const anular = useMutation({
    mutationFn: () =>
      api.post<Comprobante>(`/compras/${anulando?.id}/anular`, { motivo: motivo.trim() }),
    onSuccess: () => {
      notificar.exito('Comprobante anulado');
      setAnulando(null);
      setMotivo('');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['compras'] });
    },
    onError: (causa) => notificar.error(mensajeDe(causa, 'No se pudo anular')),
  });

  function refrescar() {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['compras'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['proveedores'] });
  }

  const columnas: Columna<Comprobante>[] = [
    {
      clave: 'comprobante',
      encabezado: 'Comprobante',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="importe block text-xs font-semibold text-armazon-800">{c.numero}</span>
          <span className="block text-[10px] uppercase text-armazon-400">{c.tipoEtiqueta}</span>
        </span>
      ),
    },
    {
      clave: 'proveedor',
      encabezado: 'Proveedor',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs uppercase text-armazon-800">
            {c.proveedorRazonSocial}
          </span>
          <span className="importe block text-[10px] text-armazon-400">
            {c.proveedorIdentificacion}
          </span>
        </span>
      ),
    },
    {
      clave: 'fecha',
      encabezado: 'Emisión',
      alinear: 'centro',
      clase: 'w-28',
      contenido: (c) => (
        <span className="importe text-[11px] text-armazon-600">{enFecha(c.fechaEmision)}</span>
      ),
    },
    {
      clave: 'sustento',
      encabezado: 'Sustento',
      contenido: (c) => (
        <span className="block text-[10px] uppercase leading-tight text-armazon-500">
          {c.sustentoEtiqueta}
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      clase: 'w-32',
      contenido: (c) => (
        <span className="block">
          <span
            className={`importe block text-xs font-bold ${
              c.estado === 'anulada' ? 'text-armazon-300 line-through' : 'text-armazon-900'
            }`}
          >
            {enDinero(c.importes.total ?? 0)}
          </span>
          {(c.importes.iva ?? 0) > 0 && (
            <span className="block text-[10px] uppercase text-armazon-400">
              IVA {enDinero(c.importes.iva ?? 0)}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      clase: 'w-28',
      contenido: (c) => (
        <span className="block">
          <span
            className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${
              c.estado === 'anulada'
                ? 'bg-armazon-100 text-armazon-500'
                : 'bg-marca-50 text-marca-700'
            }`}
          >
            {c.estadoEtiqueta}
          </span>

          {!c.cuadra && c.estado !== 'anulada' && (
            <span className="mt-0.5 flex items-center justify-center gap-1 text-[10px] font-bold uppercase text-amber-700">
              <AlertTriangle className="size-3" aria-hidden />
              No cuadra
            </span>
          )}
          {!c.datosCompletos && c.estado !== 'anulada' && (
            <span className="mt-0.5 block text-[10px] font-bold uppercase text-armazon-400">
              Espera su XML
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-24',
      contenido: (c) => (
        <span className="flex justify-end gap-1">
          <BotonDeFila icono={Eye} titulo="Ver el comprobante" alPulsar={() => setAbierto(c)} />
          {puedeGestionar && c.estado !== 'anulada' && (
            <BotonDeFila icono={Ban} titulo="Anular" alPulsar={() => setAnulando(c)} />
          )}
        </span>
      ),
    },
  ];

  const datos = resumen.data;

  return (
    <>
      <EncabezadoDePagina
        icono={ShoppingCart}
        color="indigo"
        titulo="Compras"
        subtitulo="Lo que se recibe al comprar · de aquí salen la retención y el ATS"
        acciones={
          puedeGestionar && (
            <span className="flex gap-2">
              <button
                type="button"
                onClick={() => setCargandoListado(true)}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-armazon-300 bg-white px-4 text-xs font-bold uppercase text-armazon-700 shadow-sm transition-colors hover:bg-armazon-50"
              >
                <ListChecks className="size-4" aria-hidden />
                Listado del SRI
              </button>
              <button
                type="button"
                onClick={() => setImportando(true)}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700"
              >
                <Upload className="size-4" aria-hidden />
                Importar XML
              </button>
            </span>
          )
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra
          icono={Receipt}
          rotulo="Comprobantes"
          valor={datos ? datos.comprobantes.toLocaleString('es-EC') : '…'}
          pie={datos && datos.anulados > 0 ? `${datos.anulados} ANULADOS APARTE` : 'EN EL PERÍODO'}
          tono="azul"
        />
        <Cifra
          icono={FileText}
          rotulo="Subtotal"
          valor={datos ? enDinero(datos.subtotal) : '…'}
          pie="SIN IMPUESTOS"
          tono="pizarra"
        />
        <Cifra
          icono={FileText}
          rotulo="IVA"
          valor={datos ? enDinero(datos.iva) : '…'}
          pie="DE LOS COMPROBANTES RECIBIDOS"
          tono="pizarra"
        />

        <Cifra
          icono={ListChecks}
          rotulo="Esperan su XML"
          valor={datos ? datos.sinSuXml.toLocaleString('es-EC') : '…'}
          pie={
            datos && datos.sinCuadrar > 0
              ? `Y ${datos.sinCuadrar} SIN CUADRAR`
              : 'VINIERON DEL LISTADO DEL SRI'
          }
          tono={datos && (datos.sinSuXml > 0 || datos.sinCuadrar > 0) ? 'ambar' : 'pizarra'}
        />
      </div>

      <Tabla
        datos={listado.data}
        cargando={listado.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="comprobantes"
        iconoVacio={ShoppingCart}
        mensajeVacio="Ningún comprobante en el período"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={texto}
              alCambiar={(v) => {
                setTexto(v);
                setPagina(0);
              }}
              marcador="Proveedor, número o clave de acceso…"
              className="w-72"
            />
            <input
              type="date"
              value={desde}
              max={hasta}
              onChange={(e) => {
                setDesde(e.target.value);
                setPagina(0);
              }}
              aria-label="Desde"
              className="h-9 rounded-lg border border-armazon-200 px-2 text-xs text-armazon-700"
            />
            <span className="text-[11px] uppercase text-armazon-400">a</span>
            <input
              type="date"
              value={hasta}
              min={desde}
              onChange={(e) => {
                setHasta(e.target.value);
                setPagina(0);
              }}
              aria-label="Hasta"
              className="h-9 rounded-lg border border-armazon-200 px-2 text-xs text-armazon-700"
            />
            <label className="flex items-center gap-2 text-[11px] font-semibold uppercase text-armazon-600">
              <input
                type="checkbox"
                checked={soloPendientes}
                onChange={(e) => {
                  setSoloPendientes(e.target.checked);
                  setPagina(0);
                }}
                className="size-4 rounded border-armazon-300"
              />
              Solo los que esperan su XML
            </label>
            <select
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value as EstadoDeCompra | '');
                setPagina(0);
              }}
              aria-label="Qué estado"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600 outline-none focus:border-marca-400"
            >
              <option value="">TODOS LOS ESTADOS</option>
              {(catalogos.data?.estados ?? []).map((e) => (
                <option key={e.valor} value={e.valor}>
                  {e.etiqueta.toLocaleUpperCase('es')}
                </option>
              ))}
            </select>
          </span>
        }
      />

      {importando && (
        <PanelDeImportacion
          sustentos={catalogos.data?.sustentos ?? []}
          alCerrar={() => setImportando(false)}
          alImportar={(r) => {
            setImportando(false);
            refrescar();
            setAbierto(r.comprobante);
          }}
        />
      )}

      {cargandoListado && (
        <PanelDelListado
          sustentos={catalogos.data?.sustentos ?? []}
          alCerrar={() => setCargandoListado(false)}
          alImportar={refrescar}
        />
      )}

      {abierto && (
        <DetalleDelComprobante id={abierto.id} alCerrar={() => setAbierto(null)} />
      )}

      {anulando && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-armazon-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-sm font-bold uppercase text-armazon-900">
              Anular {anulando.numero}
            </h2>
            <p className="mt-2 text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
              No se borra: se anula. Hubo un comprobante y tiene que constar que lo hubo, con el
              motivo por el que dejó de contar.
            </p>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder="Por qué se anula…"
              className="mt-3 w-full rounded-lg border border-armazon-200 px-3 py-2 text-xs uppercase outline-none focus:border-marca-400"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAnulando(null);
                  setMotivo('');
                }}
                className="inline-flex h-10 items-center rounded-lg border border-armazon-200 px-4 text-xs font-semibold uppercase text-armazon-700 hover:bg-armazon-50"
              >
                Dejarlo
              </button>
              <button
                type="button"
                disabled={motivo.trim().length < 10 || anular.isPending}
                onClick={() => anular.mutate()}
                className="inline-flex h-10 items-center rounded-lg bg-red-700 px-4 text-xs font-bold uppercase text-white hover:bg-red-800 disabled:opacity-40"
              >
                Anular
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { enFecha };
